import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from './supabaseClient';
import SEOMeta from './SEOMeta';
import { fuzzySearchPlants } from './plantSearchUtils';
import { slugify } from './slugUtils';

const formatPrice = (val) => {
  if (!val) return '—';
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return '$' + num.toFixed(2);
};

const isMobile = () => window.innerWidth <= 600;

const PlantSearch = () => {
  const [plants, setPlants] = useState([]);
  const [headerPhotoMap, setHeaderPhotoMap] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Read ?q= from URL on load
  const urlQuery = new URLSearchParams(location.search).get('q') || '';
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [sizeFilter, setSizeFilter] = useState('');
  const [saleTypeFilter, setSaleTypeFilter] = useState('');
  const [sortMode, setSortMode] = useState('popular');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mobile, setMobile] = useState(isMobile());
  const inputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchHeaderPhotos = async () => {
      const { data } = await supabase.from('plant_header_photos').select('*');
      if (data) {
        const map = {};
        data.forEach(p => { map[p.common_name.toLowerCase()] = p.image_url; });
        setHeaderPhotoMap(map);
      }
    };
    const fetchPlants = async () => {
      let all = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        const { data } = await supabase.from('plants').select('*').eq('status', 'approved').range(from, from + pageSize - 1);
        if (!data || data.length === 0) break;
        all = all.concat(data);
        if (data.length < pageSize) break;
        from += pageSize;
      }
      if (all.length > 0) setPlants(all);
    };
    fetchHeaderPhotos();
    fetchPlants();
  }, []);

  // Suggestions for dropdown
  const suggestions = searchQuery.length > 0
    ? fuzzySearchPlants(searchQuery, plants, 10).map(m => m.common_name)
    : [];

  // Group all sales by plant name
  const groupedPlants = plants.reduce((acc, plant) => {
    const name = plant.common_name?.trim();
    if (!name) return acc;
    if (!acc[name]) acc[name] = [];
    acc[name].push(plant);
    return acc;
  }, {});

  // Build fuzzy match set for current query
  const fuzzyMatchedNames = searchQuery.trim()
    ? new Set(
        fuzzySearchPlants(searchQuery, plants, 9999)
          .map(m => m.common_name.toLowerCase())
      )
    : null;

  // Check if query has any direct matches (name, genus, scientific name)
  // If it does, skip fuzzy matching entirely to avoid false positives
  const hasDirectMatches = Object.entries(groupedPlants).some(([name, entries]) => {
    const val = searchQuery.toLowerCase().trim();
    if (!val) return false;
    if (name.toLowerCase().includes(val)) return true;
    if (entries.some(e => e.genus && e.genus.toLowerCase().includes(val))) return true;
    if (entries.some(e => e.scientific_name && e.scientific_name.toLowerCase().includes(val))) return true;
    return false;
  });

  // Filter plants
  const filteredPlants = Object.entries(groupedPlants).filter(([name, entries]) => {
    const val = searchQuery.toLowerCase().trim();

    // No search — show everything
    if (!val) return true;

    // Exact substring match on name
    if (name.toLowerCase().includes(val)) return true;

    // Match on genus
    if (entries.some(e => e.genus && e.genus.toLowerCase().includes(val))) return true;

    // Match on scientific name
    if (entries.some(e => e.scientific_name && e.scientific_name.toLowerCase().includes(val))) return true;

    // Only use fuzzy match if there are NO direct matches (handles typos like "moncera" → "Monstera")
    if (!hasDirectMatches && fuzzyMatchedNames && fuzzyMatchedNames.has(name.toLowerCase())) return true;

    return false;
  })
  .filter(([name, entries]) => {
    // Sale type filter
    if (saleTypeFilter && !entries.some(p => p.sale_type && p.sale_type.toLowerCase().includes(saleTypeFilter.toLowerCase()))) return false;
    // Size filter (only when Potted)
    if (saleTypeFilter === 'Potted' && sizeFilter && !entries.some(p => p.sale_size === sizeFilter)) return false;
    return true;
  })
  .sort((a, b) => {
    if (sortMode === 'az') return a[0].localeCompare(b[0]);
    return b[1].length - a[1].length;
  });

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ paddingBottom: '60px' }}>
      <SEOMeta
        title="Search Plant Prices"
        description={`Search ${plants.length > 0 ? Math.floor(plants.length / 100) * 100 + '+' : ''} real plant sale records. Find sold prices for rare houseplants including Monstera, Alocasia, Philodendron, Hoya, and more.`}
        url="/search"
      />

      {/* HERO HEADER */}
      <div style={{
        width: '100%',
        background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)',
        padding: mobile ? '48px 24px 40px' : '64px 24px 56px',
        textAlign: 'center',
        marginBottom: '40px',
      }}>
        <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>
          🌿 Real Data. Real Prices.
        </div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '32px' : '52px', color: '#f5f0e8', fontWeight: 700, lineHeight: 1.15, margin: '0 0 16px' }}>
          Search Plant Prices
        </h1>
        <p style={{ color: 'rgba(245,240,232,0.75)', fontFamily: 'Lato, sans-serif', fontSize: mobile ? '15px' : '18px', fontWeight: 300, maxWidth: '520px', margin: '0 auto 36px', lineHeight: 1.7 }}>
          Browse real sold prices from actual plant collectors. Type a name or genus to get started.
        </p>

        {/* Search bar inside hero */}
        <div ref={inputRef} style={{ maxWidth: '640px', margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'flex', background: '#fff', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', overflow: 'visible' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
                setHighlightedIndex(-1);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex(i => Math.max(i - 1, -1));
                } else if (e.key === 'Enter') {
                  if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                    setSearchQuery(suggestions[highlightedIndex]);
                  }
                  setShowSuggestions(false);
                  setHighlightedIndex(-1);
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                  setHighlightedIndex(-1);
                }
              }}
              onFocus={() => searchQuery && setShowSuggestions(true)}
              placeholder="e.g. Monstera, Alocasia, Fiddle Leaf..."
              style={{ flex: 1, padding: '18px 20px', border: 'none', outline: 'none', fontSize: '16px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', borderRadius: '16px 0 0 16px', background: 'transparent' }}
            />
            <button
              onClick={() => setShowSuggestions(false)}
              style={{ padding: '18px 28px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', border: 'none', color: '#f5f0e8', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', borderRadius: '0 16px 16px 0', whiteSpace: 'nowrap' }}
            >
              Search
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #4a7c59', borderRadius: '12px', listStyle: 'none', margin: '6px 0 0', padding: '6px', zIndex: 1000, maxHeight: '240px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(44,74,46,0.2)', textAlign: 'left' }}>
              {suggestions.map((name, idx) => (
                <li
                  key={name}
                  onMouseDown={() => {
                    navigate(`/plant/${slugify(name)}`);
                    setShowSuggestions(false);
                    setHighlightedIndex(-1);
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderRadius: '8px', color: '#1e2d1f', fontSize: '15px', fontFamily: 'Lato, sans-serif', background: highlightedIndex === idx ? '#f0f7f1' : 'transparent', fontWeight: highlightedIndex === idx ? 700 : 400 }}
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* FILTERS + COUNT + SORT */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: mobile ? '0 12px' : '0 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', flexDirection: mobile ? 'column' : 'row', alignItems: 'flex-end', flexWrap: 'wrap' }}>

          <div style={{ minWidth: mobile ? 'unset' : '200px' }}>
            <label style={{ display: 'block', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '6px', fontFamily: 'Lato, sans-serif' }}>Filter by type</label>
            <select
              value={saleTypeFilter}
              onChange={(e) => { setSaleTypeFilter(e.target.value); if (e.target.value !== 'Potted') setSizeFilter(''); }}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #c4a882', borderRadius: '12px', fontSize: mobile ? '16px' : '15px', background: '#ffffff', color: '#1e2d1f', outline: 'none', fontFamily: 'Lato, sans-serif', cursor: 'pointer', boxSizing: 'border-box' }}
            >
              <option value="">All Types</option>
              <option value="Potted">Potted</option>
              <option value="Cutting">Cutting</option>
              <option value="Plug/Starter">Plug/Starter</option>
              <option value="Tissue Culture">Tissue Culture (TC)</option>
            </select>
          </div>

          {saleTypeFilter === 'Potted' && (
            <div style={{ minWidth: mobile ? 'unset' : '160px' }}>
              <label style={{ display: 'block', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '6px', fontFamily: 'Lato, sans-serif' }}>Filter by size</label>
              <select
                value={sizeFilter}
                onChange={(e) => setSizeFilter(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #c4a882', borderRadius: '12px', fontSize: mobile ? '16px' : '15px', background: '#ffffff', color: '#1e2d1f', outline: 'none', fontFamily: 'Lato, sans-serif', cursor: 'pointer', boxSizing: 'border-box' }}
              >
                <option value="">All Sizes</option>
                <option value="2">2"</option>
                <option value="3">3"</option>
                <option value="4">4"</option>
                <option value="6">6"</option>
                <option value="8">8"</option>
                <option value="10">10"</option>
                <option value="12">12"</option>
              </select>
            </div>
          )}

          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {plants.length > 0 && (
              <span style={{ fontSize: '13px', color: '#4a5e4b', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>
                Showing <strong>{filteredPlants.length}</strong> plant{filteredPlants.length !== 1 ? 's' : ''}
              </span>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setSortMode('popular')} style={{ padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif', background: sortMode === 'popular' ? 'linear-gradient(135deg, #2c4a2e, #4a7c59)' : '#f0f7f1', color: sortMode === 'popular' ? '#f5f0e8' : '#4a7c59' }}>🔥 Popular</button>
              <button onClick={() => setSortMode('az')} style={{ padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif', background: sortMode === 'az' ? 'linear-gradient(135deg, #2c4a2e, #4a7c59)' : '#f0f7f1', color: sortMode === 'az' ? '#f5f0e8' : '#4a7c59' }}>A-Z</button>
            </div>
          </div>
        </div>
      </div>

      {/* PLANT GRID */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: mobile ? '0 12px' : '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: mobile ? '12px' : '20px' }}>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes shimmer {
              0% { background-position: -400px 0; }
              100% { background-position: 400px 0; }
            }
            .skeleton {
              background: linear-gradient(90deg, #e8f0e9 25%, #d4e6d6 50%, #e8f0e9 75%);
              background-size: 400px 100%;
              animation: shimmer 1.4s ease-in-out infinite;
              border-radius: 8px;
            }
          `}</style>
          {plants.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: '48px', display: 'inline-block', animation: 'spin 1.5s linear infinite' }}>🌿</div>
              <p style={{ color: '#4a7c59', fontFamily: 'Lato, sans-serif', marginTop: '16px', fontSize: '15px' }}>Loading plants...</p>
            </div>
          ) : filteredPlants.length === 0 && searchQuery ? (
            <p style={{ color: '#4a7c59', fontStyle: 'italic', fontFamily: 'Lato, sans-serif' }}>No plants found matching "{searchQuery}".</p>
          ) : (
            filteredPlants.map(([plantName, entries]) => {
              const sortedEntries = [...entries].sort((a, b) => new Date(a.date_sold) - new Date(b.date_sold));
              const recentSale = sortedEntries[sortedEntries.length - 1];
              return (
                <a
                  key={plantName}
                  href={`/plant/${slugify(plantName)}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/plant/${slugify(plantName)}`); }}
                  style={{ background: '#ffffff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', textDecoration: 'none', display: 'block' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(44,74,46,0.16)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(44,74,46,0.08)'; }}
                >
                  {headerPhotoMap[plantName.toLowerCase()]
                    ? <img src={headerPhotoMap[plantName.toLowerCase()]} alt={plantName} loading="lazy" style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: '160px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>🌿</div>
                  }
                  <div style={{ padding: mobile ? '16px' : '20px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '17px' : '19px', color: '#1e2d1f', marginBottom: '14px', lineHeight: 1.2 }}>
                      {plantName}
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px', fontFamily: 'Lato, sans-serif' }}>
                      <span style={{ color: '#4a5e4b' }}>Latest sale</span>
                      <span style={{ fontWeight: 700, color: '#2c4a2e' }}>{formatPrice(recentSale.sold_price)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontFamily: 'Lato, sans-serif' }}>
                      <span style={{ color: '#4a5e4b' }}>Total sales</span>
                      <span style={{ fontWeight: 700, color: '#2c4a2e' }}>{entries.length}</span>
                    </div>
                  </div>
                </a>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PlantSearch;